import type { EvergreenChatModeOrigin, LiveChatMode } from '@/features/live/ablySessionChannels';

const CHAT_MODES: readonly LiveChatMode[] = ['PUBLIC', 'PRIVATE', 'DISABLED'];
const ORIGINS: readonly EvergreenChatModeOrigin[] = ['TIMELINE', 'MANUAL'];

function isLiveChatMode(value: unknown): value is LiveChatMode {
  return CHAT_MODES.includes(value as LiveChatMode);
}

/** Révision propre à la session, attribuée par le back à chaque changement de mode appliqué. */
export type ChatModeVersion = { origin: EvergreenChatModeOrigin; revision: number };

export function readChatModeVersion(origin: unknown, revision: unknown): ChatModeVersion | null {
  if (
    !ORIGINS.includes(origin as EvergreenChatModeOrigin) ||
    typeof revision !== 'number' ||
    !Number.isSafeInteger(revision) ||
    revision < 0
  ) {
    return null;
  }
  return { origin: origin as EvergreenChatModeOrigin, revision };
}

export type ChatModeChange = { chatMode: LiveChatMode; version: ChatModeVersion | null };

/**
 * Un Evergreen issu d'un live rejoue les changements de mode du chat au timecode où ils ont eu lieu :
 * le back publie alors cet événement sur le canal de la session, comme pour un changement manuel.
 */
export function readChatModeChange(
  messageName: string | undefined,
  data: unknown,
): ChatModeChange | null {
  if (messageName !== 'event-message' || !data || typeof data !== 'object') {
    return null;
  }
  const { type, chatMode, origin, revision } = data as {
    type?: unknown;
    chatMode?: unknown;
    origin?: unknown;
    revision?: unknown;
  };
  if (type !== 'CHAT_MODE_CHANGED' || !isLiveChatMode(chatMode)) {
    return null;
  }
  return { chatMode, version: readChatModeVersion(origin, revision) };
}

export type ReplayedChatMode = {
  sessionIdentifier: string;
  chatMode: LiveChatMode;
  version?: ChatModeVersion | null;
};

/**
 * Le mode rejoué arrive par trois voies sans ordre garanti : l'événement Ably, la réponse au
 * changement manuel et la configuration relue en HTTP, qui peut être plus ancienne que l'écran.
 * Versionné, un changement ne remplace que l'état d'une révision inférieure, et un choix manuel
 * n'est jamais remplacé par la timeline. Sans révision (back qui ne la fournit pas), une réponse
 * HTTP ne s'applique que si aucun événement n'est arrivé depuis son départ.
 */
export type EvergreenChatModeSync = {
  replayed: ReplayedChatMode | null;
  eventCount: number;
  latestRequest: { requestId: string; sessionIdentifier: string; eventCountAtStart: number } | null;
};

export const initialEvergreenChatModeSync: EvergreenChatModeSync = {
  replayed: null,
  eventCount: 0,
  latestRequest: null,
};

function supersedes(current: ReplayedChatMode | null, next: ReplayedChatMode): boolean {
  if (!current || current.sessionIdentifier !== next.sessionIdentifier || !current.version) {
    return true;
  }
  if (!next.version) {
    return false;
  }
  if (current.version.origin === 'MANUAL' && next.version.origin === 'TIMELINE') {
    return false;
  }
  return next.version.revision > current.version.revision;
}

export function chatModeEventReceived(
  sync: EvergreenChatModeSync,
  replayed: ReplayedChatMode,
): EvergreenChatModeSync {
  if (!supersedes(sync.replayed, replayed)) {
    return sync;
  }
  return { ...sync, replayed, eventCount: sync.eventCount + 1 };
}

export function chatModeRequestStarted(
  sync: EvergreenChatModeSync,
  requestId: string,
  sessionIdentifier: string,
): EvergreenChatModeSync {
  return {
    ...sync,
    latestRequest: { requestId, sessionIdentifier, eventCountAtStart: sync.eventCount },
  };
}

export function chatModeRequestFulfilled(
  sync: EvergreenChatModeSync,
  requestId: string,
  replayed: Omit<ReplayedChatMode, 'chatMode'> & { chatMode: LiveChatMode | null | undefined },
): EvergreenChatModeSync {
  const { chatMode } = replayed;
  if (!chatMode) {
    return sync;
  }
  const next = { ...replayed, chatMode };
  if (!isOrderedByRevision(sync, next)) {
    const { latestRequest } = sync;
    if (latestRequest?.requestId !== requestId) {
      return sync;
    }
    if (!next.version && latestRequest.eventCountAtStart !== sync.eventCount) {
      return sync;
    }
  }
  return supersedes(sync.replayed, next) ? { ...sync, replayed: next } : sync;
}

/**
 * Deux relectures de la même session peuvent revenir dans le désordre : seule la révision départage,
 * quelle que soit la plus récente. Sans état affiché, la session de la dernière relecture dit si la
 * réponse concerne encore la session ouverte.
 */
function isOrderedByRevision(sync: EvergreenChatModeSync, next: ReplayedChatMode): boolean {
  if (!next.version) {
    return false;
  }
  const { replayed, latestRequest } = sync;
  if (!replayed) {
    return latestRequest?.sessionIdentifier === next.sessionIdentifier;
  }
  return replayed.sessionIdentifier === next.sessionIdentifier && !!replayed.version;
}

export function replayedChatModeFor(
  replayed: ReplayedChatMode | null,
  sessionIdentifier: string | null | undefined,
): LiveChatMode | null {
  return replayed && replayed.sessionIdentifier === sessionIdentifier ? replayed.chatMode : null;
}

type RegieChatSettings = { chatMode: LiveChatMode };

/**
 * En Evergreen, la régie affiche le mode rejoué, celui que le serveur applique aux participants,
 * et n'écrit jamais le réglage du webinaire, qui sert de mode de départ aux sessions suivantes.
 */
export function regieDisplayedSettings<T extends RegieChatSettings>(
  settings: T,
  isEvergreen: boolean,
  replayedChatMode: LiveChatMode | null,
): T {
  return isEvergreen && replayedChatMode ? { ...settings, chatMode: replayedChatMode } : settings;
}

/**
 * En Evergreen, un changement de mode passe par la commande de session : il ne vaut que pour la
 * session en cours et prend le pas sur les changements rejoués.
 */
export function evergreenChatModeCommand<T extends RegieChatSettings>(
  next: T,
  displayed: T,
  isEvergreen: boolean,
): LiveChatMode | null {
  return isEvergreen && next.chatMode !== displayed.chatMode ? next.chatMode : null;
}

export function regieSettingsToSave<T extends RegieChatSettings>(
  next: T,
  isEvergreen: boolean,
  webinarChatMode: LiveChatMode,
): T {
  return isEvergreen ? { ...next, chatMode: webinarChatMode } : next;
}

type AttendeeChatModeContext = {
  isEvergreen: boolean;
  /** Mode rejoué de la session Evergreen affichée, absent tant que rien ne l'a fourni. */
  evergreenAttendeeChatMode?: LiveChatMode | null;
  /** Réglage poussé en direct par la régie. */
  liveOverrideChatMode?: LiveChatMode | null;
  sessionChatMode?: LiveChatMode | null;
};

export function resolveAttendeeChatMode({
  isEvergreen,
  evergreenAttendeeChatMode,
  liveOverrideChatMode,
  sessionChatMode,
}: AttendeeChatModeContext): LiveChatMode {
  if (isEvergreen && evergreenAttendeeChatMode) {
    return evergreenAttendeeChatMode;
  }
  return liveOverrideChatMode ?? sessionChatMode ?? 'PUBLIC';
}
